import React, { useEffect, useMemo, useState } from 'react';
import { Clock, AlertTriangle, Info, Lock } from 'lucide-react';

export interface ResponseWindowPhase {
  /** Scadenza della fase. */
  until: Date;
  /** Testo prima del conto alla rovescia, es. "Puoi rispondere per". */
  label: string;
  /** Spiegazione mostrata al passaggio del mouse o al click sull'icona. */
  help: string;
  /** Fase gia' "di riserva" (es. operatore umano): parte in ambra. */
  warn?: boolean;
}

interface ResponseWindowTimerProps {
  /** Fasi in ordine: la prima non scaduta e' quella mostrata. Vuoto = nessuna finestra aperta. */
  phases: ResponseWindowPhase[];
  /** Testo e spiegazione quando tutte le fasi sono scadute (o nessuna e' mai iniziata). */
  expired: { label: string; help: string };
  /** Chiamato quando una fase scade (per ricalcolare lo stato del composer). */
  onPhaseChange?: () => void;
  className?: string;
}

const HOUR = 3_600_000;

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86_400);
  const h = Math.floor((total % 86_400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (days > 0) return `${days}g ${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/**
 * Conto alla rovescia della finestra di risposta (Meta: 24 ore, poi 7 giorni
 * come operatore umano; WhatsApp Cloud: 24 ore). Scorre ogni secondo, cambia
 * colore quando si avvicina la scadenza (ambra sotto le 6 ore, rosso sotto
 * l'ora) e spiega la regola al passaggio del mouse o al click sull'icona.
 */
export function ResponseWindowTimer({ phases, expired, onPhaseChange, className = '' }: ResponseWindowTimerProps) {
  const [now, setNow] = useState(() => Date.now());
  // Spiegazione: visibile al passaggio del mouse, fissata con il click (utile da tastiera e su touch).
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const showHelp = hover || pinned;

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const activeIndex = useMemo(() => phases.findIndex((p) => p.until.getTime() > now), [phases, now]);
  const active = activeIndex >= 0 ? phases[activeIndex] : null;

  // Avvisa il composer quando si passa di fase (o si scade).
  const [lastIndex, setLastIndex] = useState(activeIndex);
  useEffect(() => {
    if (activeIndex !== lastIndex) {
      setLastIndex(activeIndex);
      onPhaseChange?.();
    }
  }, [activeIndex, lastIndex, onPhaseChange]);

  const remaining = active ? active.until.getTime() - now : 0;
  const tone = !active
    ? 'expired'
    : remaining <= HOUR
      ? 'danger'
      : remaining <= 6 * HOUR || active.warn
        ? 'warn'
        : 'ok';

  const toneClass = {
    ok: 'text-[#787774] dark:text-gray-400',
    warn: 'text-amber-700 dark:text-amber-400',
    danger: 'text-red-600 dark:text-red-400',
    expired: 'text-[#787774] dark:text-gray-400',
  }[tone];

  const Icon = tone === 'expired' ? Lock : tone === 'danger' ? AlertTriangle : Clock;
  const help = active ? active.help : expired.help;

  return (
    <div className={`relative inline-flex items-center gap-1.5 text-[11px] ${toneClass} ${className}`}>
      <Icon className="h-3 w-3 flex-shrink-0" />
      {active ? (
        <span>
          {active.label}{' '}
          <span className={`font-mono font-semibold tabular-nums ${tone === 'danger' ? 'animate-gentle-pulse' : ''}`} aria-live="off">
            {formatCountdown(remaining)}
          </span>
        </span>
      ) : (
        <span>{expired.label}</span>
      )}
      <button
        type="button"
        onClick={() => setPinned((v) => !v)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onBlur={() => setPinned(false)}
        className="p-0.5 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10"
        aria-label="Come funziona la finestra di risposta"
        aria-expanded={showHelp}
      >
        <Info className="h-3 w-3" />
      </button>
      {showHelp && (
        <div
          role="tooltip"
          className="absolute left-0 bottom-full mb-1.5 z-30 w-72 px-3 py-2 rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-[#202020] text-[11px] leading-relaxed text-[#37352F] dark:text-gray-200 shadow-lg animate-fade-in"
        >
          {help}
        </div>
      )}
    </div>
  );
}

/** Fasi Meta (Instagram / Messenger) a partire dall'ultimo messaggio in arrivo. */
export function metaWindowPhases(lastInboundAt: string | null | undefined): ResponseWindowPhase[] {
  if (!lastInboundAt) return [];
  const last = new Date(lastInboundAt).getTime();
  return [
    {
      until: new Date(last + 24 * HOUR),
      label: 'Puoi rispondere per',
      help:
        'Regola di Meta: dopo un messaggio del contatto hai 24 ore per rispondere liberamente. Il conto alla rovescia parte dall’ultimo messaggio ricevuto e riparte a ogni nuovo messaggio del contatto.',
    },
    {
      until: new Date(last + 7 * 24 * HOUR),
      label: 'Solo come operatore umano, per',
      warn: true,
      help:
        'Passate le 24 ore, Meta permette di rispondere fino a 7 giorni con il tag "operatore umano", che l’app deve avere approvato (Human Agent). Se non è approvato, Meta rifiuta il messaggio.',
    },
  ];
}

export const META_WINDOW_EXPIRED = {
  label: 'Finestra di risposta scaduta',
  help: 'Sono passati più di 7 giorni dall’ultimo messaggio del contatto (o non ha mai scritto): per la regola di Meta non puoi scrivere per primo. Potrai rispondere quando il contatto invia un nuovo messaggio.',
};
